import { ContextualTabbedSettings } from '@components'

import { DateTimeFormat, useModal } from '@billy/design-system'
import { Button, Tag, XStack, YStack } from '@shinetools/lumen-react'
import { Check, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EnableEInvoicingModal } from '@features/attachments/elements/EnableEInvoicingModal'
import { NemHandelDisableModal } from '@features/eInvoicing/elements/NemHandelDisableModal'
import { NemHandelNetworkConfig } from '@features/eInvoicingSettings/types'

import { ModalId } from '../../../../../enums/modalId'
import { getCurrentLocale } from '../../../../../utils'
import { EInvoicingSettingsComponent } from '../../NullableEInvoicingSettings'
import { LearnMoreTranslation } from '../elements/LearnMoreTranslation'

const { ContextualSettingInput, CopySettingsButton, MainSettingCta } = ContextualTabbedSettings

export const NemHandelNetworkSettings: EInvoicingSettingsComponent<NemHandelNetworkConfig> = ({
  networkSettings,
  context,
}) => {
  const { t } = useTranslation()
  const locale = getCurrentLocale()
  const { config, actions, isLoading } = networkSettings

  const { open: openEnableEInvoicingModal } = useModal(ModalId.EnableEInvoicingModal)
  const { open: openDisableEInvoicingModal } = useModal(ModalId.DisableEInvoicingModal)

  if (isLoading) {
    return <ContextualSettingInput isLoading />
  }

  return (
    <YStack gap={16}>
      <MainSettingCta
        description={
          <LearnMoreTranslation
            i18nKey="einvoicing.settings.activation.activate_description"
            linkKey="einvoicing.settings.activation.activate_link_dk"
          />
        }
        ctaContent={
          <XStack gap={8} align="center">
            {actions.deactivate ? (
              <>
                {config.activationDate && (
                  <Tag
                    icon={<Check />}
                    style={{ maxHeight: 'fit-content' }}
                    color="success"
                    type="subtle"
                    size="xsmall"
                    label={t('einvoicing.settings.activation.activation_date', {
                      date: DateTimeFormat.getFormattedDate(config.activationDate, locale),
                    })}
                  />
                )}
                <Button
                  variant="outlined"
                  label={t('einvoicing.settings.activation.deactivate')}
                  onClick={() => openDisableEInvoicingModal()}
                />
              </>
            ) : (
              <Button
                label={t('einvoicing.settings.activation.activate_cta')}
                onClick={() => openEnableEInvoicingModal()}
              />
            )}
          </XStack>
        }
      />

      <ContextualSettingInput
        icon={Info}
        label={t('einvoicing.settings.dk.cvr_label', { cvr: config.cvr })}
        description={
          <LearnMoreTranslation
            i18nKey="einvoicing.settings.dk.cvr_registration_hint"
            linkKey="einvoicing.settings.dk.cvr_registration_hint_link"
          />
        }
      >
        <CopySettingsButton textToCopy={config.cvr ?? ''} />
      </ContextualSettingInput>

      <EnableEInvoicingModal id={ModalId.EnableEInvoicingModal} />
      <NemHandelDisableModal />
    </YStack>
  )
}
